import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * Sticky conversion bar. It duplicates the nav, hero and section CTAs, so it
 * earns its place only late in the page: it appears once the reader is ~60% down
 * and steps aside as the final CTA section approaches. It also checks the area
 * behind its own footprint and fades whenever readable copy would be covered.
 */
export default function StickyCTA() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let frameId;

    const hasReadableCopyBehind = () => {
      const barWidth = Math.min(window.innerWidth * 0.92, 920);
      const barHeight = window.innerWidth >= 640 ? 88 : 72;
      const bottom = window.innerWidth >= 640 ? 20 : 12;
      const left = (window.innerWidth - barWidth) / 2;
      const top = window.innerHeight - bottom - barHeight;
      const xSamples = [0.12, 0.3, 0.5, 0.7, 0.88].map((ratio) => left + barWidth * ratio);
      const ySamples = [0.25, 0.55, 0.82].map((ratio) => top + barHeight * ratio);
      const readableSelector = 'p, h1, h2, h3, h4, li, summary, blockquote';

      return ySamples.some((y) =>
        xSamples.some((x) =>
          document.elementsFromPoint(x, y).some((element) => {
            if (element.closest('[data-sticky-cta]')) return false;
            return Boolean(element.closest(readableSelector));
          })
        )
      );
    };

    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const pct = max > 0 ? window.scrollY / max : 0;
      const cta = document.getElementById('final-cta');
      const ctaApproaching = cta
        ? cta.getBoundingClientRect().top < window.innerHeight * 0.85
        : false;
      setVisible(pct > 0.6 && !ctaApproaching && !hasReadableCopyBehind());
    };

    const onScroll = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <div
      data-sticky-cta
      aria-hidden={!visible}
      className={`fixed bottom-3 left-3 right-3 z-40 rounded-[22px] border border-[#F5F3EF]/[0.16] bg-[#0A1A2F]/[0.92] px-4 py-3 text-[#F5F3EF] backdrop-blur-2xl transition-[opacity,transform] duration-300 sm:bottom-5 sm:left-1/2 sm:right-auto sm:w-[min(92vw,920px)] sm:-translate-x-1/2 sm:px-5 sm:py-4 ${
        visible ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-4 opacity-0'
      }`}
      style={{ boxShadow: '0 24px 70px -28px rgba(0,0,0,0.85), inset 0 1px rgba(255,255,255,0.1)' }}
    >
      <div className="max-w-[1180px] mx-auto flex items-center justify-between gap-4">
        <p className="text-[#F5F3EF]/70 text-[13px] sm:text-[14px] hidden sm:block">
          Don&rsquo;t go invisible · <span className="font-serif italic text-[#D4AF37]">let’s talk</span>
        </p>
        <Link
          to="/contact"
          tabIndex={visible ? 0 : -1}
          className="flex-1 sm:flex-initial inline-flex items-center justify-center rounded-full bg-[#F5F3EF] px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#0A1A2F] transition-colors hover:bg-[#D4AF37] sm:px-8 sm:py-4"
        >
          See If You&rsquo;re Being Recommended
        </Link>
      </div>
    </div>
  );
}
